import { z } from 'zod';
import { WorkstreamState, WorkstreamStateSchema } from '@/pages/OperationsAdministration/types/WorkstreamState';

const gridColumnRegistry = z.registry<{
    title?: string;
    gridColumnType?: string;
    showInStates?: WorkstreamState[];
}>();

export const WorkstreamDefinitionOperationConfigGridRowSchema = z.object({
    workstreamDefinitionId: z.int32(),
    name: z.string().register(gridColumnRegistry, {
        title: 'Workstream Definition',
        gridColumnType: 'name',
        showInStates: [ 'enabled', 'disabled' ]
    }),
    timezone: z.string().register(gridColumnRegistry, {
        title: 'Timezone',
        gridColumnType: 'timezone',
        showInStates: [ 'enabled', 'disabled' ]
    }),
    enabledAt: z.optional(z.date()).register(gridColumnRegistry, {
        title: 'Enabled On',
        gridColumnType: 'formattedDateTime',
        showInStates: ['enabled']
    }),
    backlogEffectiveFrom: z.date().register(gridColumnRegistry, {
        title: 'Backlog Effective Date',
        gridColumnType: 'formattedDate',
        showInStates: [ 'enabled', 'disabled' ]
    }),
    lastRunAt: z.optional(z.date()).register(gridColumnRegistry, {
        title: 'Last Snapshot Run',
        gridColumnType: 'formattedDateTime',
        showInStates: ['enabled']
    }),
    nextRunAt: z.optional(z.date()).register(gridColumnRegistry, {
        title: 'Next Snapshot Run',
        gridColumnType: 'formattedDateTime',
        showInStates: ['enabled']
    }),
    sources: z.array(z.string()).register(gridColumnRegistry, {
        title: 'Integration Sources',
        gridColumnType: 'sources',
        showInStates: [ 'enabled', 'disabled' ]
    }),
    state: WorkstreamStateSchema.register(gridColumnRegistry, {
        title: 'Status',
        gridColumnType: 'status',
        showInStates: [ 'enabled', 'disabled' ]
    }),
    isDirty: z.boolean().default(false)
});

export type WorkstreamDefinitionOperationConfigGridRow = z.infer<
    typeof WorkstreamDefinitionOperationConfigGridRowSchema
>;

export function getColumnsForState(state: WorkstreamState) {
    return Object.entries(WorkstreamDefinitionOperationConfigGridRowSchema.shape)
        .filter(([ _field, schema ]) => gridColumnRegistry.get(schema)?.showInStates?.includes(state))
        .map(([ field, schema ]) => {
            const meta = gridColumnRegistry.get(schema);
            return {
                field: field,
                headerName: meta?.title,
                type: meta?.gridColumnType
            };
        });
}

export function updateState(ws: WorkstreamDefinitionOperationConfigGridRow | undefined, newState: WorkstreamState) {
    return ws
        ? {
            ...ws, state: newState, isDirty: !ws.isDirty
        }
        : undefined;
}